import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import Styles from "./Footer.module.scss";
import { TrustPilotWidget } from "@/shared/components/TrustPilot/TrustPilotComponents";
import {
  becomePartnerLink,
  companyLink,
  contactLink,
  dataprivacyLink,
  faqLink,
  imprintLink,
  jobsLink,
  mediaLink,
  offerElectricityLink,
  offerGasLink,
} from "../Header/Navigation/Links";
import classNames from "classnames";
import { useFlavour } from "@/shared/flavours/useFlavour";
import Icon from "@/shared/components/Icon/Icon";
import { AccessibilityRoute } from "@/routes/content";

const SocialLinks = {
  Instagram: "https://www.instagram.com/remindme_official",
  TikTok: "https://www.tiktok.com/@remind.me_de?_t=8msyapa9hSK&_r=1",
  Facebook: "https://www.facebook.com/profile.php?id=61560404879009",
  Youtube: "https://www.youtube.com/@remindme_de",
} as const;

// Add type for UC_UI
declare global {
  interface Window {
    UC_UI?: {
      showSecondLayer: () => void;
    };
  }
}

const Footer = () => {
  const { FooterLogo } = useFlavour();
  const { t } = useTranslation();

  return (
    <footer className={Styles.footer} id="footerNavigation" role="contentinfo">
      <div className={Styles.topSection}>
        <div className={Styles.logoPlaceholder}>
          <img src={FooterLogo} className={Styles.logo} height="30" width="92" alt="remind.me footer logo" />
        </div>
        <nav className={Styles.lists} aria-label="Footer navigation">
          <div className={Styles.listGroup}>
            <ul>
              <li>
                <Link to={companyLink} data-cy="footer-unternehmen-link">
                  {t("footer.aboutUs")}
                </Link>
              </li>
              <li>
                <Link to={faqLink} data-cy="footer-faq-link">
                  {t("footer.faq")}
                </Link>
              </li>
              <li>
                <Link to={contactLink} data-cy="footer-kontakt-link">
                  {t("footer.contact")}
                </Link>
              </li>
              <li>
                <Link to={AccessibilityRoute} data-cy="footer-barrierefreiheitserklarung-link">
                  {t("footer.accessibility")}
                </Link>
              </li>
            </ul>
          </div>
          <div className={Styles.listGroup}>
            <ul>
              <li>
                <Link to={imprintLink} data-cy="footer-impressum-link">
                  {t("footer.imprint")}
                </Link>
              </li>
              <li>
                <Link to={dataprivacyLink} data-cy="footer-data-link">
                  {t("footer.dataPrivacy")}
                </Link>
              </li>
              <li>
                <Link to={mediaLink} data-cy="footer-presse-link">
                  {t("footer.media")}
                </Link>
              </li>
            </ul>
          </div>
          <div className={Styles.listGroup}>
            <ul>
              <li>
                <Link to={jobsLink} data-cy="footer-jobs-link">
                  {t("footer.jobs")}
                </Link>
              </li>
              <li>
                <div
                  aria-label={t("footer.cookieSettings")}
                  role="button"
                  className={Styles.cookieButton}
                  data-cy="footer-cookie-settings-link"
                  onClick={() => window.UC_UI?.showSecondLayer()}
                  tabIndex={0}
                >
                  {t("footer.cookieSettings")}
                </div>
              </li>
              <li>
                <Link to={becomePartnerLink} data-cy="footer-become-partner-link">
                  {t("footer.becomePartner")}
                </Link>
              </li>
            </ul>
          </div>
        </nav>
        <div className={classNames(Styles.logoTrustPilot, Styles.placeholder)}>
          <TrustPilotWidget templateId={"trustpilotDefault"} />
        </div>
      </div>
      <div className={Styles.bottomSection}>
        <div className={Styles.placeholder} />
        <nav className={Styles.lists} aria-label="Footer services">
          <div className={Styles.listGroup}>
            <ul>
              <li className={Styles.groupName}>{t("footer.electricity")}</li>
              <li>
                <Link to={offerElectricityLink} data-cy="footer-cheap-electricity-link">
                  {t("footer.cheapElectricity")}
                </Link>
              </li>
            </ul>
          </div>
          <div className={Styles.listGroup}>
            <ul>
              <li className={Styles.groupName}>{t("footer.gas")}</li>
              <li>
                <Link to={offerGasLink} data-cy="footer-cheap-gas-link">
                  {t("footer.cheapGas")}
                </Link>
              </li>
            </ul>
          </div>
          <div className={Styles.listGroup}>
            <ul>
              <li className={Styles.groupName}>{t("footer.findUsHere")}</li>
              <li>
                <ul className={Styles.socialIconsContainer}>
                  <li>
                    <Link
                      target="_blank"
                      to={SocialLinks.Instagram}
                      aria-label={`${t("footer.findUsHere")} Instagram`}
                      rel="noopener noreferrer"
                    >
                      <Icon name="instagram" aria-hidden="true" />
                    </Link>
                  </li>
                  <li>
                    <Link
                      target="_blank"
                      to={SocialLinks.TikTok}
                      aria-label={`${t("footer.findUsHere")} TikTok`}
                      rel="noopener noreferrer"
                    >
                      <Icon name="tiktok" aria-hidden="true" />
                    </Link>
                  </li>
                  <li>
                    <Link
                      target="_blank"
                      to={SocialLinks.Facebook}
                      aria-label={`${t("footer.findUsHere")} Facebook`}
                      rel="noopener noreferrer"
                    >
                      <Icon name="facebook" aria-hidden="true" />
                    </Link>
                  </li>
                  <li>
                    <Link
                      target="_blank"
                      to={SocialLinks.Youtube}
                      aria-label={`${t("footer.findUsHere")} YouTube`}
                      rel="noopener noreferrer"
                    >
                      <Icon name="youtube" aria-hidden="true" />
                    </Link>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </nav>
        <div className={Styles.placeholder}></div>
      </div>
      <div className={Styles.signature}>
        <span>{t("footer.copyright", { year: new Date().getFullYear() })}</span>
      </div>
    </footer>
  );
};

export default Footer;
